import React, { memo, MutableRefObject, Ref, useCallback, useMemo, useRef, useState } from 'react'

import './SegmentedTimeline.scss'

const ROOT_FONT_PX = 16
const toRem = (px: number) => `${parseFloat((px / ROOT_FONT_PX).toFixed(4))}rem`

// Falls back to the same muted purple-gray as $color-font-text-primary-text-purple-gray
// when a bucket segment doesn't specify its own color.
const DEFAULT_SEGMENT_COLOR = '#6e6b86'
// $color-base-purple-15. The band reads as a purple wash over the range, not a
// grey panel, so it stays legible against the bars it sits behind. Kept as an
// inline style (not the .scss file) so it stays testable: identity-obj-proxy
// mocks .scss in Jest, so class-only styles never reach getComputedStyle.
const SELECTION_BG_COLOR = '#efedfc'
// Same reasoning as SELECTION_BG_COLOR above — kept inline so the dimmed
// state is assertable in tests.
const BAR_DIMMED_OPACITY = 0.5
const DEFAULT_BARS_HEIGHT_PX = 60
const DEFAULT_BAR_GAP_PX = 4
const DEFAULT_TOOLTIP_FLIP_RATIO = 0.85

const HAIRLINE_BORDER_PX = 1
const BARS_TOP_PX = 8
const AXIS_HEIGHT_PX = 18
const BAND_TOP_PX = 2
const TOOLTIP_TOP_PX = 2
const TOOLTIP_OFFSET_PX = 10
const HANDLE_WIDTH_PX = 10
const HANDLE_HEIGHT_PX = 20
const AXIS_EDGE_RATIO = 0.03

export interface TimelineSegment {
  key: string
  count: number
  color?: string
  hoverColor?: string
}

export interface TimelineBucket {
  start: number
  end?: number
  total?: number
  segments: TimelineSegment[]
}

export interface TimelineAxisTick {
  time: number
  ratio: number
  label: string
}

export interface TimelineSelection {
  leftPct: number
  rightPct: number
}

export interface SegmentedTimelineProps {
  buckets: TimelineBucket[]
  maxTotal?: number
  hoverIndex?: number | null
  renderTooltipContent?: (bucket: TimelineBucket, index: number) => React.ReactNode
  showTooltip?: boolean
  hoverRatio?: number
  tooltipFlipRatio?: number
  selection?: TimelineSelection | null
  showSelectionHandles?: boolean
  showBaseline?: boolean
  axisTicks?: TimelineAxisTick[]
  isLoading?: boolean
  barsHeightPx?: number
  barGapPx?: number
  stripRef?: Ref<HTMLDivElement>
  onMouseMove?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: () => void
  className?: string
  style?: React.CSSProperties
  ariaLabel?: string
  testIdPrefix?: string
}

const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1)

const bucketTotal = (bucket: TimelineBucket) =>
  bucket.total ?? bucket.segments.reduce((sum, segment) => sum + segment.count, 0)

const segmentColor = (segment: TimelineSegment, isHovered: boolean) =>
  (isHovered ? segment.hoverColor ?? segment.color : segment.color) ?? DEFAULT_SEGMENT_COLOR

interface TimelineBarProps {
  bucket: TimelineBucket
  // Rendered only as data-count for tests/inspection — it plays no part in the
  // height calculation (that uses segment.count / scaleMax), hence the name.
  dataCount: number
  scaleMax: number
  isHovered: boolean
  isDimmed: boolean
  testId: string
}

const TimelineBar = memo(function TimelineBar({
  bucket,
  dataCount,
  scaleMax,
  isHovered,
  isDimmed,
  testId,
}: TimelineBarProps) {
  return (
    <div
      className="SegmentedTimeline__bar-column"
      style={{ opacity: isDimmed ? BAR_DIMMED_OPACITY : 1 }}
      data-test-id={testId}
      data-count={dataCount}
    >
      {bucket.segments.map(segment =>
        segment.count > 0 ? (
          <div
            key={segment.key}
            className="SegmentedTimeline__bar-segment"
            style={{
              height: `${(segment.count / scaleMax) * 100}%`,
              background: segmentColor(segment, isHovered),
            }}
          />
        ) : null,
      )}
    </div>
  )
})

export const SegmentedTimeline = memo(function SegmentedTimeline({
  buckets,
  maxTotal,
  hoverIndex,
  renderTooltipContent,
  showTooltip = true,
  hoverRatio,
  tooltipFlipRatio = DEFAULT_TOOLTIP_FLIP_RATIO,
  selection = null,
  showSelectionHandles = true,
  showBaseline = true,
  axisTicks = [],
  isLoading = false,
  barsHeightPx = DEFAULT_BARS_HEIGHT_PX,
  barGapPx = DEFAULT_BAR_GAP_PX,
  stripRef,
  onMouseMove,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
  className,
  style,
  ariaLabel,
  testIdPrefix = 'segmented-timeline',
}: SegmentedTimelineProps) {
  const internalStripRef = useRef<HTMLDivElement | null>(null)
  const [internalHoverIndex, setInternalHoverIndex] = useState<number | null>(null)

  const isHoverControlled = hoverIndex !== undefined
  const activeHoverIndex = isHoverControlled ? hoverIndex : internalHoverIndex

  const setStripNode = useCallback(
    (node: HTMLDivElement | null) => {
      internalStripRef.current = node
      if (typeof stripRef === 'function') {
        stripRef(node)
      } else if (stripRef) {
        ;(stripRef as MutableRefObject<HTMLDivElement | null>).current = node
      }
    },
    [stripRef],
  )

  const scaleMax = useMemo(() => {
    if (maxTotal != null) {
      return Math.max(maxTotal, 1)
    }
    return Math.max(...buckets.map(bucketTotal), 1)
  }, [buckets, maxTotal])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isHoverControlled && !isLoading) {
        const rect = internalStripRef.current?.getBoundingClientRect()
        if (rect?.width && buckets.length) {
          const ratio = clamp01((e.clientX - rect.left) / rect.width)
          setInternalHoverIndex(Math.min(Math.floor(ratio * buckets.length), buckets.length - 1))
        }
      }
      onMouseMove?.(e)
    },
    [isHoverControlled, isLoading, buckets.length, onMouseMove],
  )

  const handleMouseLeave = useCallback(() => {
    if (!isHoverControlled) {
      setInternalHoverIndex(null)
    }
    onMouseLeave?.()
  }, [isHoverControlled, onMouseLeave])

  const hoveredBucket = activeHoverIndex != null ? buckets[activeHoverIndex] : undefined
  const tooltipVisible = !!renderTooltipContent && showTooltip && !isLoading && !!hoveredBucket

  const activeHoverRatio =
    hoverRatio ??
    (activeHoverIndex != null && buckets.length ? (activeHoverIndex + 0.5) / buckets.length : 0)
  const axisHeightPx = axisTicks.length ? AXIS_HEIGHT_PX : 0
  const axisTopPx = BARS_TOP_PX + barsHeightPx
  const bandHeightPx = axisTopPx - BAND_TOP_PX
  // The baseline sits in the axis row's first pixel, so it only adds height when
  // there is no axis to absorb it.
  const baselineHeightPx = showBaseline ? HAIRLINE_BORDER_PX : 0

  return (
    <div
      className={`SegmentedTimeline${className ? ` ${className}` : ''}`}
      style={{ height: toRem(axisTopPx + Math.max(axisHeightPx, baselineHeightPx)), ...style }}
      aria-label={ariaLabel}
      data-test-id={testIdPrefix}
    >
      {selection && (
        <div
          className="SegmentedTimeline__selection-band"
          style={{
            left: `${selection.leftPct}%`,
            width: `${Math.max(selection.rightPct - selection.leftPct, 0)}%`,
            height: toRem(bandHeightPx),
            background: SELECTION_BG_COLOR,
          }}
          data-test-id={`${testIdPrefix}-selection-overlay`}
        />
      )}

      <div className="SegmentedTimeline__bar-strip-outer" style={{ height: toRem(barsHeightPx) }}>
        <div
          ref={setStripNode}
          className={`SegmentedTimeline__bar-strip${isLoading ? ' SegmentedTimeline__bar-strip--loading' : ''}`}
          style={{ gap: toRem(barGapPx) }}
          onMouseMove={handleMouseMove}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseLeave={handleMouseLeave}
          data-test-id={`${testIdPrefix}-bar-strip`}
        >
          {buckets.map((bucket, index) => (
            <TimelineBar
              key={`${bucket.start}-${index}`}
              bucket={bucket}
              dataCount={bucketTotal(bucket)}
              scaleMax={scaleMax}
              isHovered={index === activeHoverIndex}
              isDimmed={activeHoverIndex != null && index !== activeHoverIndex}
              testId={`${testIdPrefix}-bucket-${index}`}
            />
          ))}
        </div>
      </div>

      {showBaseline && (
        <div
          className="SegmentedTimeline__baseline"
          style={{ top: toRem(axisTopPx) }}
          data-test-id={`${testIdPrefix}-baseline`}
          aria-hidden="true"
        />
      )}

      {selection && showSelectionHandles && (
        <>
          <div
            className="SegmentedTimeline__selection-handle"
            style={{
              left: `${selection.leftPct}%`,
              top: toRem(BAND_TOP_PX + (bandHeightPx - HANDLE_HEIGHT_PX) / 2),
              marginLeft: toRem(-HANDLE_WIDTH_PX / 2),
            }}
            data-test-id={`${testIdPrefix}-handle-start`}
            aria-hidden="true"
          >
            <span className="SegmentedTimeline__handle-grip" />
            <span className="SegmentedTimeline__handle-grip" />
          </div>
          <div
            className="SegmentedTimeline__selection-handle"
            style={{
              left: `${selection.rightPct}%`,
              top: toRem(BAND_TOP_PX + (bandHeightPx - HANDLE_HEIGHT_PX) / 2),
              marginLeft: toRem(-HANDLE_WIDTH_PX / 2),
            }}
            data-test-id={`${testIdPrefix}-handle-end`}
            aria-hidden="true"
          >
            <span className="SegmentedTimeline__handle-grip" />
            <span className="SegmentedTimeline__handle-grip" />
          </div>
        </>
      )}

      {tooltipVisible && (
        <div
          className="SegmentedTimeline__tooltip"
          style={{
            top: toRem(TOOLTIP_TOP_PX),
            left: `${activeHoverRatio * 100}%`,
            transform:
              activeHoverRatio > tooltipFlipRatio
                ? `translateX(calc(-100% - ${TOOLTIP_OFFSET_PX}px))`
                : `translateX(${TOOLTIP_OFFSET_PX}px)`,
          }}
          data-test-id={`${testIdPrefix}-tooltip`}
        >
          {renderTooltipContent(hoveredBucket, activeHoverIndex ?? 0)}
        </div>
      )}

      {!!axisTicks.length && (
        <div
          className="SegmentedTimeline__axis-row"
          style={{ top: toRem(axisTopPx), height: toRem(axisHeightPx) }}
          data-test-id={`${testIdPrefix}-axis`}
        >
          {axisTicks.map((tick, index) => {
            const align = tick.ratio < AXIS_EDGE_RATIO ? 'start' : tick.ratio > 1 - AXIS_EDGE_RATIO ? 'end' : 'center'
            return (
              <span
                key={`${tick.time}-${index}`}
                className={`SegmentedTimeline__axis-tick SegmentedTimeline__axis-tick--${align}`}
                style={{ left: `${tick.ratio * 100}%` }}
                data-test-id={`${testIdPrefix}-axis-tick-${index}`}
              >
                {tick.label}
              </span>
            )
          })}
        </div>
      )}
    </div>
  )
})

export const TimelineTooltipHeader: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="SegmentedTimeline__tooltip-header">{children}</div>
)

export const TimelineTooltipRow: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="SegmentedTimeline__tooltip-row">{children}</div>
)

export const TimelineTooltipDot: React.FC<{ color: string }> = ({ color }) => (
  <span className="SegmentedTimeline__tooltip-dot" style={{ background: color }} />
)

export default SegmentedTimeline
